import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { organization, useSession } from "../lib/auth.ts";

// Mirrors LoginPage: only honour same-origin paths, reject absolute URLs.
function safeNext(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/")) return "/";
  // Reject protocol-relative paths and back-slash variants — Chrome
  // normalises `/\evil.com` and `\/evil.com` into protocol-relative
  // forms, so the bare `//` check below isn't enough.
  if (raw.length >= 2 && (raw[1] === "/" || raw[1] === "\\")) return "/";
  return raw;
}

// /pair is server-rendered (Vite proxies to API), not a React Router
// route, so we must do a real browser navigation rather than nav().
function isServerRendered(path: string): boolean {
  return path === "/pair" || path.startsWith("/pair?") || path.startsWith("/pair/");
}

export function OrgSetupPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const { refetch } = useSession();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="center">
      <div className="card narrow">
        <h2>Create your organisation</h2>
        <p className="muted">
          One organisation owns its devices and test results. You can be a
          member of multiple later.
        </p>
        <form
          className="formstack"
          onSubmit={async (e) => {
            e.preventDefault();
            setErr(null);
            setBusy(true);
            const created = await organization.create({
              name,
              slug: slug || name.toLowerCase().replace(/\s+/g, "-"),
            });
            if (created.error) {
              setBusy(false);
              setErr(created.error.message ?? "Failed to create");
              return;
            }
            const setActive = await organization.setActive({
              organizationId: created.data?.id,
            });
            if (setActive.error) {
              setBusy(false);
              setErr(setActive.error.message ?? "Failed to set active");
              return;
            }
            // disableCookieCache forces /get-session to re-read the DB
            // instead of serving the pre-setActive session out of the
            // short-lived cookie cache. Without this, RequireActiveOrg
            // would still see activeOrganizationId=null and bounce us
            // back here.
            await refetch({ query: { disableCookieCache: true } });
            if (isServerRendered(next)) {
              window.location.assign(next);
            } else {
              nav(next);
            }
          }}
        >
          <label>
            Organisation name
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label>
            Slug (URL handle, optional)
            <input
              value={slug}
              placeholder={name.toLowerCase().replace(/\s+/g, "-")}
              onChange={(e) => setSlug(e.target.value)}
            />
          </label>
          {err && <div className="err">{err}</div>}
          <button type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create organisation"}
          </button>
        </form>
      </div>
    </div>
  );
}
